import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, CalendarDays, Clock, ArrowRight, CheckCircle } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";
import { format, addDays, isBefore, startOfToday } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = ["Programmable LED Display", "Light Box", "Channel Letters", "Neon Sign", "Non-Illuminated Sign", "Non-Illuminated 3D Letters", "Pylon Sign", "Window/Car/Wall Graphics", "Banners", "Sign Repair", "Electrical Services", "Sign Installation", "City Sign Permit"];

export default function BookingPage() {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_interest: "", notes: "", time_slot: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!selectedDate) return;
    const fetchSlots = async () => {
      setLoadingSlots(true);
      try {
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        const res = await axios.get(`${API}/bookings/available-slots?date=${dateStr}`);
        setAvailableSlots(res.data.available_slots);
      } catch {
        toast.error("Failed to load available slots");
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.time_slot) {
      toast.error("Please fill in all required fields and select a time slot");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        date: format(selectedDate, "yyyy-MM-dd"),
      });
      setSuccess(true);
      toast.success("Consultation booked successfully!");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("This time slot was just booked. Please select another.");
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        const res = await axios.get(`${API}/bookings/available-slots?date=${dateStr}`);
        setAvailableSlots(res.data.available_slots);
        setForm({ ...form, time_slot: "" });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="booking-success" className="min-h-screen flex items-center justify-center pt-20">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-10 h-10 text-gold" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-white mb-4">Booking Confirmed!</h2>
          <p className="text-white/50 mb-2">Your free consultation is scheduled for:</p>
          <p className="text-gold font-bold text-lg mb-1">{format(selectedDate, "EEEE, MMMM d, yyyy")}</p>
          <p className="text-gold font-bold text-lg mb-8">{form.time_slot}</p>
          <p className="text-white/40 text-sm mb-8">We'll contact you at {form.phone} to confirm the details.</p>
          <a href="tel:+17783220801" data-testid="booking-success-phone" className="inline-flex items-center gap-2 bg-gold text-black font-bold px-6 py-3 rounded-full text-sm">
            <Phone className="w-4 h-4" /> Call Us: (778) 322-0801
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page">
      <section className="pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-gold text-sm tracking-[0.3em] uppercase font-semibold mb-4">
            Book a Consultation
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-heading text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter uppercase text-white mb-6">
            Free On-Site<br /><span className="gold-shimmer">Consultation</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-lg text-white/50 max-w-2xl">
            Schedule a free consultation with our signage experts. We'll visit your location and provide a custom quote tailored to your needs.
          </motion.p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Step indicators */}
          <div className="flex items-center gap-4 mb-12" data-testid="booking-steps">
            {["Select Date", "Choose Time", "Your Details"].map((label, i) => (
              <div key={label} className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step > i + 1 ? "bg-gold text-black" : step === i + 1 ? "bg-gold text-black" : "bg-white/10 text-white/40"}`}>
                  {step > i + 1 ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`text-sm hidden sm:block ${step === i + 1 ? "text-white" : "text-white/40"}`}>{label}</span>
                {i < 2 && <div className="w-8 md:w-16 h-px bg-white/10"></div>}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Calendar */}
            <div className="lg:col-span-2">
              {step === 1 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card rounded-2xl p-8" data-testid="booking-calendar-section">
                  <div className="flex items-center gap-3 mb-6">
                    <CalendarDays className="w-5 h-5 text-gold" />
                    <h3 className="font-heading text-xl font-bold text-white">Select a Date</h3>
                  </div>
                  <div className="flex justify-center booking-calendar">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setForm({ ...form, time_slot: "" });
                      }}
                      disabled={(date) => isBefore(date, startOfToday()) || date.getDay() === 0}
                      fromDate={new Date()}
                      toDate={addDays(new Date(), 60)}
                      className="rounded-lg border border-white/10 p-4"
                      data-testid="booking-calendar"
                    />
                  </div>
                  {selectedDate && (
                    <div className="mt-6 text-center">
                      <p className="text-white/50 text-sm mb-4">Selected: <span className="text-gold font-semibold">{format(selectedDate, "EEEE, MMMM d, yyyy")}</span></p>
                      <button onClick={() => setStep(2)} data-testid="booking-next-step-1" className="bg-gold text-black font-bold px-8 py-3 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all text-sm flex items-center gap-2 mx-auto">
                        Next: Choose Time <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </motion.div>
              )}

              {step === 2 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card rounded-2xl p-8" data-testid="booking-time-section">
                  <div className="flex items-center gap-3 mb-6">
                    <Clock className="w-5 h-5 text-gold" />
                    <h3 className="font-heading text-xl font-bold text-white">Choose a Time Slot</h3>
                  </div>
                  <p className="text-white/50 text-sm mb-6">{format(selectedDate, "EEEE, MMMM d, yyyy")}</p>
                  {loadingSlots ? (
                    <div className="text-center py-8 text-white/50">Loading available slots...</div>
                  ) : availableSlots.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-white/50 mb-4">No available slots for this date.</p>
                      <button onClick={() => setStep(1)} className="text-gold text-sm font-bold">Choose Another Date</button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot}
                          onClick={() => setForm({ ...form, time_slot: slot })}
                          data-testid={`time-slot-${slot.replace(/\s/g, '-')}`}
                          className={`py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                            form.time_slot === slot
                              ? "bg-gold text-black"
                              : "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
                          }`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button onClick={() => setStep(1)} className="text-white/50 hover:text-white text-sm">Back</button>
                    {form.time_slot && (
                      <button onClick={() => setStep(3)} data-testid="booking-next-step-2" className="bg-gold text-black font-bold px-8 py-3 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all text-sm flex items-center gap-2 ml-auto">
                        Next: Your Details <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card rounded-2xl p-8" data-testid="booking-details-section">
                  <h3 className="font-heading text-xl font-bold text-white mb-6">Your Details</h3>
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="text-white/60 text-xs block mb-2">Full Name *</label>
                        <input
                          type="text"
                          data-testid="booking-name"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm placeholder:text-white/20 focus:ring-1 focus:ring-gold/50 transition-all outline-none"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="text-white/60 text-xs block mb-2">Email *</label>
                        <input
                          type="email"
                          data-testid="booking-email"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm placeholder:text-white/20 focus:ring-1 focus:ring-gold/50 transition-all outline-none"
                          placeholder="john@company.com"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="text-white/60 text-xs block mb-2">Phone *</label>
                        <input
                          type="tel"
                          data-testid="booking-phone"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm placeholder:text-white/20 focus:ring-1 focus:ring-gold/50 transition-all outline-none"
                          placeholder="(778) 000-0000"
                        />
                      </div>
                      <div>
                        <label className="text-white/60 text-xs block mb-2">Service Interest</label>
                        <select
                          data-testid="booking-service"
                          value={form.service_interest}
                          onChange={(e) => setForm({ ...form, service_interest: e.target.value })}
                          className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm focus:ring-1 focus:ring-gold/50 transition-all outline-none"
                        >
                          <option value="" className="bg-[#131a23]">Select...</option>
                          {services.map((s) => (
                            <option key={s} value={s} className="bg-[#131a23]">{s}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mb-6">
                      <label className="text-white/60 text-xs block mb-2">Additional Notes</label>
                      <textarea
                        data-testid="booking-notes"
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={3}
                        className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm placeholder:text-white/20 focus:ring-1 focus:ring-gold/50 transition-all outline-none resize-none"
                        placeholder="Tell us about your project..."
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <button type="button" onClick={() => setStep(2)} className="text-white/50 hover:text-white text-sm">Back</button>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="booking-submit-btn"
                        className="flex-1 bg-gold text-black font-bold py-4 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {submitting ? "Booking..." : "Confirm Booking"}
                      </button>
                    </div>
                  </form>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="glass-card rounded-2xl p-6">
                <h4 className="font-heading text-lg font-bold text-white mb-4">Booking Summary</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Date</span>
                    <span className="text-white font-medium">{selectedDate ? format(selectedDate, "MMM d, yyyy") : "Not selected"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Time</span>
                    <span className="text-white font-medium">{form.time_slot || "Not selected"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Type</span>
                    <span className="text-gold font-medium">Free Consultation</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Duration</span>
                    <span className="text-white font-medium">~30 min</span>
                  </div>
                </div>
                <div className="gold-line my-4"></div>
                <div className="flex justify-between">
                  <span className="text-white/50 text-sm">Cost</span>
                  <span className="text-gold font-bold">FREE</span>
                </div>
              </div>

              <div className="glass-card rounded-2xl p-6">
                <h4 className="font-heading text-sm font-bold text-white mb-3">What to Expect</h4>
                <ul className="space-y-2">
                  {["On-site visit at your location", "Expert assessment of your needs", "Custom design recommendations", "Detailed quote with no obligations"].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-xs text-white/60">
                      <CheckCircle className="w-3 h-3 text-gold mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="glass-card rounded-2xl p-6 text-center">
                <p className="text-white/50 text-xs mb-3">Prefer to call?</p>
                <a href="tel:+17783220801" data-testid="booking-sidebar-phone" className="inline-flex items-center gap-2 text-gold font-bold text-sm">
                  <Phone className="w-4 h-4" /> (778) 322-0801
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 section-alt border-t border-white/5" data-testid="booking-cta">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-5">
            Have Questions First?
          </h2>
          <p className="text-white/50 text-base mb-8">
            Contact us directly and our team will help you find the perfect signage solution.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+17783220801" data-testid="booking-cta-phone" className="bg-gold text-black font-bold px-8 py-4 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all duration-300 hover:-translate-y-1 flex items-center gap-2">
              <Phone className="w-4 h-4" /> Call (778) 322-0801
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
